import { useEffect, useRef, type ReactNode } from 'react';
import { Modal, View, Text, Pressable, Animated } from 'react-native';
import { warmupIntroSheetStyles as styles } from './WarmupIntroSheet.styles';
import { WordleIntroBody, HiloIntroBody, FootballerIntroBody, KeeperDuelIntroBody } from './WarmupIntroBodies';
import {
  WARMUP_INTRO_CONTENT,
  WARMUP_INTRO_TEST_IDS as IDS,
  type WarmupIntroMode,
} from '../../../src/quiz/constants/warmupIntros.constants';

interface WarmupIntroSheetProps {
  visible: boolean;
  /** Which mode's intro to render; null renders nothing. */
  mode: WarmupIntroMode | null;
  /** Primary CTA — close and start playing. */
  onPlay: () => void;
  /** Backdrop "tap anywhere to skip" — same dismiss path as Play. */
  onSkip: () => void;
}

const BODY = {
  wordle: WordleIntroBody,
  hilo: HiloIntroBody,
  footballer: FootballerIntroBody,
  keeperduel: KeeperDuelIntroBody,
} satisfies Record<WarmupIntroMode, () => ReactNode>;

/**
 * First-run how-to-play bottom-sheet. Dimmed backdrop (tap to skip) + a bottom sheet that slides up,
 * with a per-mode demo body and a single Play CTA. Both Play and the backdrop call the one dismiss path
 * so "mark seen" can never be bypassed. See specs/warmup-game-intros/spec.md.
 */
export function WarmupIntroSheet({ visible, mode, onPlay, onSkip }: WarmupIntroSheetProps) {
  const translateY = useRef(new Animated.Value(40)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.spring(translateY, { toValue: 0, tension: 60, friction: 9, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }),
      ]).start();
    } else {
      translateY.setValue(40);
      opacity.setValue(0);
    }
  }, [visible, translateY, opacity]);

  if (!mode) return null;
  const content = WARMUP_INTRO_CONTENT[mode];
  const Body = BODY[mode];

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onSkip}>
      <View style={styles.overlay}>
        <Pressable
          testID={IDS.backdrop}
          style={styles.backdrop}
          onPress={onSkip}
          accessibilityRole="button"
          accessibilityLabel="Skip tutorial"
        />
        <Text testID={IDS.skipHint} style={styles.skipHint}>
          Tap anywhere to skip
        </Text>

        <Animated.View testID={IDS.sheet} style={[styles.sheet, { opacity, transform: [{ translateY }] }]}>
          <View style={styles.handle} />

          <View style={styles.header}>
            <Text style={[styles.eyebrow, { color: content.eyebrowColor }]}>
              {content.eyebrow.toUpperCase()}
            </Text>
            <Text style={styles.title}>{content.title}</Text>
          </View>

          <Body />

          <View style={styles.footer}>
            <Text style={styles.footerGlyph}>{content.footerGlyph}</Text>
            <Text style={styles.footerText}>{content.footer}</Text>
          </View>

          <Pressable
            testID={IDS.playButton}
            onPress={onPlay}
            style={({ pressed }) => [styles.playButton, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel="Play"
          >
            <Text style={styles.playButtonText}>Play</Text>
          </Pressable>
        </Animated.View>
      </View>
    </Modal>
  );
}
